"use client";
import { Url } from "next/dist/shared/lib/router/router";
import { PropsWithChildren, useMemo, useCallback } from "react";
import { useRouterChange } from "./useRouterChange";
// eslint-disable-next-line no-restricted-imports
import NextLink from "next/link";
import { useLang, useDefaultLocale } from "@/providers/langProvider";
import { useRemoveLangFromPath } from "@/hooks/useRemoveLangFromPath";

interface Props extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick"> {
  href: Url;
  onClick?: (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  passHref?: boolean;
  prefetch?: boolean;
  skipCheckingForLang?: boolean;
  style?: React.CSSProperties;
  className?: string;
  target?: React.HTMLAttributeAnchorTarget;
  id?: string;
  title?: string;
}

export const Link: React.FC<PropsWithChildren & Props> = ({
  href,
  passHref,
  onClick,
  children,
  skipCheckingForLang,
  style,
  className,
  target,
  id,
  title,
  prefetch = false,
  ...rest
}) => {
  const { setRouterChange } = useRouterChange();
  const removeLangFromPath = useRemoveLangFromPath();
  const lang = useLang();
  const defaultLocale = useDefaultLocale();

  // Memoize href computation so it doesn't re-run on every parent re-render
  const finalHref = useMemo(() => {
    const href1 = removeLangFromPath(href.toString(), false);
    // The store's default locale is unprefixed; any other current lang gets a `/lang` prefix.
    // `defaultLocale` is server-provided (store basic_language), so SSR and the first client
    // render compute the SAME href — no hydration mismatch, and prefetch targets are exact.
    const prefix = lang === defaultLocale ? "" : `/${lang}`;
    const resolved = href1.startsWith("http")
      ? href1
      : `${prefix}${href1 === "/" && prefix !== "" ? "" : href1}`;
    return resolved || "/";
  }, [href, lang, defaultLocale, removeLangFromPath]);

  // Stable onClick — avoids creating a new function every render which would
  // force NextLink to re-attach its click handler each time.
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      setRouterChange(finalHref);
      sessionStorage.removeItem("scrollPosition");
      sessionStorage.removeItem("scrollRestorePath");
      onClick?.(e);
    },
    [finalHref, setRouterChange, onClick],
  );

  return (
    <NextLink
      id={id}
      title={title}
      style={style}
      prefetch={prefetch}
      passHref={passHref}
      href={finalHref}
      onClick={handleClick}
      className={className}
      target={target}
      {...rest}
    >
      {children}
    </NextLink>
  );
};
